// Assigning an artifact to RMS records (#11727 slice 3): the creator files
// it under any number of records from one modal — search a record, pick it,
// see and remove current assignments. A record link makes the artifact
// readable by every member of the organization from the record's page.
import React, { useEffect, useState } from "react";
import { Tooltip } from "@mui/material";
import { XIcon } from "@phosphor-icons/react";
import EntityIcon from "@/components/FpPages/RMS/Dashboard/Components/EntityIcon";
import { recordIconKind } from "@/components/FpPages/Documents/browser/records";
import { hasSoftKeyboard } from "@/utils/softKeyboard";
import EntitySearchInput from "../RMS/Dashboard/Components/EntitySearchInput";
import type { ShallowEntity } from "../RMS/Dashboard/types";
import {
  assignArtifactEntity,
  fetchArtifactEntities,
  unassignArtifactEntity,
  type ArtifactEntity,
} from "./useArtifacts";
import {
  ARTIFACT_DIALOG_WELL,
  ArtifactDialogError,
  ArtifactDialogFrame,
  ArtifactDialogSection,
} from "./ArtifactDialogFrame";

const KIND_LABELS: Record<string, string> = {
  manager: "Manager",
  vehicle: "Vehicle",
  company: "Company",
  person: "Person",
  portfolio: "Portfolio",
  deal: "Deal",
  client: "Client",
  topic: "Topic",
};

const kindLabel = (kind: string): string =>
  KIND_LABELS[kind] ?? kind.charAt(0).toUpperCase() + kind.slice(1);

const AssignArtifactDialog: React.FC<{
  artifactId: string;
  artifactTitle: string;
  onClose: () => void;
  /** Fires after every successful change so the caller can refresh. */
  onChanged?: (entities: ArtifactEntity[]) => void;
}> = ({ artifactId, artifactTitle, onClose, onChanged }) => {
  const [entities, setEntities] = useState<ArtifactEntity[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchArtifactEntities(artifactId)
      .then((items) => {
        if (!cancelled) setEntities(items);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [artifactId]);

  const apply = async (
    change: () => Promise<ArtifactEntity[]>
  ): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const next = await change();
      setEntities(next);
      onChanged?.(next);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const assigned = new Set((entities ?? []).map((e) => e.id));

  return (
    <ArtifactDialogFrame
      title="Link to record"
      subject={artifactTitle}
      onClose={onClose}
      closeLabel="Done"
    >
      <ArtifactDialogSection label="Find a record">
        <EntitySearchInput
          value={null}
          placeholder="Search managers, vehicles, companies, people…"
          autoFocus={!hasSoftKeyboard()}
          blurAfterSelect
          portalDropdown
          onSelect={(entity: ShallowEntity | null) => {
            if (!entity || assigned.has(entity.id) || busy) return;
            void apply(() => assignArtifactEntity(artifactId, entity.id));
          }}
        />
      </ArtifactDialogSection>
      <ArtifactDialogSection label="Linked records">
        {entities === null ? (
          <div className="h-10 animate-pulse rounded-[8px] bg-[var(--fp-bg-well)]" />
        ) : entities.length === 0 ? (
          <p className="m-0 text-[13px] text-[var(--fp-ink-3)]">
            Not linked to any record yet. Linking one lists this artifact on the
            record's Artifacts tab for the people it is shared with.
          </p>
        ) : (
          <ul
            className={`m-0 list-none divide-y divide-[var(--fp-divider)] p-0 ${ARTIFACT_DIALOG_WELL}`}
          >
            {entities.map((entity) => (
              <li
                key={entity.id}
                className="flex items-center gap-2.5 px-3 py-2"
              >
                <EntityIcon
                  kind={recordIconKind(entity.kind)}
                  aria-hidden
                  size={14}
                  className="flex-none text-[#2A3DCB]"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium text-[var(--fp-ink)]">
                    {entity.name}
                  </span>
                  <span className="block text-[11.5px] text-[var(--fp-ink-3)]">
                    {kindLabel(entity.kind)}
                  </span>
                </span>
                <Tooltip title="Remove" placement="top" describeChild>
                  <button
                    type="button"
                    aria-label={`Remove ${entity.name}`}
                    disabled={busy}
                    onClick={() =>
                      void apply(() =>
                        unassignArtifactEntity(artifactId, entity.id)
                      )
                    }
                    className="flex h-6 w-6 flex-none cursor-pointer items-center justify-center rounded-[var(--fp-chip-r)] text-[var(--fp-ink-3)] transition-colors hover:bg-[var(--fp-hover)] hover:text-[var(--fp-ink)] disabled:cursor-default disabled:opacity-50"
                  >
                    <XIcon size={13} aria-hidden />
                  </button>
                </Tooltip>
              </li>
            ))}
          </ul>
        )}
      </ArtifactDialogSection>
      {error && <ArtifactDialogError>{error}</ArtifactDialogError>}
    </ArtifactDialogFrame>
  );
};

export default AssignArtifactDialog;

/** The pane-header entry point (#11727 slice 3): one icon button that owns
 *  its dialog, so every artifact pane can offer "Link to record" without
 *  each one wiring the modal. */
